import data from './provider-contracts.json';

/**
 * Provider-contract status source-of-truth (launch-readiness B12 — G1/G7
 * truthfulness).
 *
 * **Post-ADR-0111 scope (2026-06-28):** the DEFAULT customer-conversation
 * provider is now Claude on Amazon Bedrock in the EU (IAM-auth, no API key, no
 * ZDR contract needed — it runs under the AWS DPA). This SOT therefore tracks
 * the ZDR status of the OPTIONAL, non-EU, key-based FAILOVER providers, where
 * `primary` = the preferred failover (first-party Anthropic) and `failover` =
 * the secondary (OpenAI). The `/trust` data-flow inference node describes the
 * Bedrock-EU default directly and no longer derives from this SOT.
 *
 * Marketing previously asserted as present-tense FACT that Anthropic + OpenAI
 * traffic runs under SIGNED Zero Data Retention contracts and that OpenAI
 * failover is live. Neither was true: the Anthropic ZDR agreement is still
 * being pursued (BL-002, operator-confirmed pending 2026-06-11) and the OpenAI
 * key is not yet seeded (BL-003), so the failover provider cannot run. Shipping
 * those claims would be a launch-blocking misrepresentation.
 *
 * This file is the single source of truth for every public ZDR / failover
 * claim. The marketing copy never hard-codes the contract state; instead each
 * affected string has a `.signed` / `.live` and a `.pending` variant in all six
 * locale catalogs, and the selectors below pick which key renders FROM THIS
 * SOT. The honest "agreement in progress / not yet active" framing ships today;
 * when the operator confirms the contract (updates `plan/BLOCKERS.md` BL-002)
 * and seeds the OpenAI key (BL-003), flipping the flags here flips the rendered
 * copy — no prose rewrite, and `provider-contracts.test.ts` locks the two in
 * lockstep so the copy can never drift ahead of reality.
 *
 * Type-only schema + compile-time JSON import, mirroring `compliance-status.ts`:
 * no zod runtime in the marketing bundle; the shape is
 * validated by the sibling test. Pure presentational data, no runtime fetch.
 */

/** Whether a provider's Zero Data Retention agreement is contractually signed. */
export type ZdrContractStatus = 'signed' | 'pending';

export interface ProviderContract {
  /** `primary` = the always-on provider; `failover` = the secondary, used only on failure. */
  readonly role: 'primary' | 'failover';
  readonly zdrContract: ZdrContractStatus;
  /**
   * Whether this provider is actually reachable in production (its key is
   * seeded). Meaningful for the `failover` provider: a failover claim is only
   * truthful when both `liveFailover` AND a signed ZDR contract hold.
   */
  readonly liveFailover: boolean;
}

export interface ProviderContractsDocument {
  readonly lastUpdated: string;
  readonly providers: Readonly<Record<string, ProviderContract>>;
}

export const providerContractsDocument: ProviderContractsDocument =
  data as ProviderContractsDocument;

const PROVIDERS = providerContractsDocument.providers;

/** A provider's ZDR contract status, or `pending` if the provider is unknown. */
export function providerZdr(provider: string): ZdrContractStatus {
  return PROVIDERS[provider]?.zdrContract ?? 'pending';
}

/** True only when EVERY provider's ZDR agreement is contractually signed. */
export function allZdrSigned(): boolean {
  return Object.values(PROVIDERS).every((p) => p.zdrContract === 'signed');
}

/**
 * True only when the failover (OpenAI) path is genuinely usable: its key is
 * seeded AND its ZDR contract is signed. Either alone is not enough to claim a
 * live, contractually-covered failover.
 */
export function openaiFailoverClaimable(): boolean {
  const openai = PROVIDERS.openai;
  return openai !== undefined && openai.liveFailover && openai.zdrContract === 'signed';
}

/**
 * The aggregate ZDR claim key (Privacy "no training" default + the security
 * questionnaire DSP row). `.signed` only when all providers are signed.
 *
 * The returned values are written as full literals so the i18n key-coverage
 * guard sees both variant keys as reachable (it scans source for literal keys).
 */
export function zdrAggregateKey(
  base: 'privacy.defaults.noTraining.body' | 'questionnaire.row.DSP.a',
): string {
  if (base === 'privacy.defaults.noTraining.body') {
    return allZdrSigned()
      ? 'privacy.defaults.noTraining.body.signed'
      : 'privacy.defaults.noTraining.body.pending';
  }
  return allZdrSigned() ? 'questionnaire.row.DSP.a.signed' : 'questionnaire.row.DSP.a.pending';
}

/** The Anthropic (primary) model-card body key — `.signed` only when its ZDR is signed. */
export function anthropicCardBodyKey(): string {
  return providerZdr('anthropic') === 'signed'
    ? 'ai.modelCards.item1.body.signed'
    : 'ai.modelCards.item1.body.pending';
}

/** The OpenAI (failover) model-card body key — `.live` only when the failover is claimable. */
export function openaiCardBodyKey(): string {
  return openaiFailoverClaimable()
    ? 'ai.modelCards.item2.body.live'
    : 'ai.modelCards.item2.body.pending';
}
